import React from 'react';
import { Product } from '../../types';
import { formatPrice, formatQuantityUnit } from '../../lib/utils';
import { motion } from 'motion/react';
import { Heart, Check, Plus, Flame, Zap, Sparkles, Tag } from 'lucide-react';
import { getProductImageProps } from '../../utils/imageOptimizer';
import { HighlightMatch } from '../HighlightMatch';

interface ProductCardProps {
  product: Product;
  onClick: (product: Product) => void;
  isFavorite?: boolean;
  onToggleFavorite?: (e: React.MouseEvent) => void;
  onAddToCart?: (product: Product) => void;
  cartQuantity?: number;
  onUpdateCartQuantity?: (productId: string, quantity: number) => void;
  onShare?: (product: Product, e: React.MouseEvent) => void;
  searchQuery?: string;
}

function ProductCard({ 
  product, 
  onClick, 
  isFavorite = false, 
  onToggleFavorite,
  onAddToCart,
  cartQuantity = 0,
  onUpdateCartQuantity,
  onShare,
  searchQuery = ''
}: ProductCardProps) {
  const discountPercent = product.mrp && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      onClick={() => onClick(product)}
      className={`relative flex flex-col justify-between bg-white rounded-2xl p-3 shadow-[0_4px_25px_rgba(0,0,0,0.03)] border border-gray-100 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:border-gray-200/60 transition-all duration-300 group cursor-pointer active:scale-[0.99] active:brightness-95 w-full ${!product.available ? 'opacity-75' : ''}`}
    >
      
      {/* Top Floating Row (Discount & Wishlist Button) */}
      <div className="absolute top-3 left-3 right-3 z-10 flex items-start justify-between pointer-events-none">
        <div className="pointer-events-auto flex flex-col items-start gap-1">
          {discountPercent > 0 && (
            <div className="bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-xs leading-none">
              {discountPercent}% OFF
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite?.(e);
          }}
          className="pointer-events-auto w-8 h-8 rounded-full bg-white shadow-[0_3px_12px_rgba(0,0,0,0.08)] border border-gray-100/50 flex items-center justify-center text-gray-400 hover:text-rose-500 transition-all hover:scale-105 active:scale-95 cursor-pointer mt-[-7px] mr-[-4px]"
          title={isFavorite ? "Remove from Favorites / पसंदीदा से हटाएं" : "Add to Favorites / पसंदीदा में जोड़ें"}
          aria-label={isFavorite ? "Remove from Favorites / पसंदीदा से हटाएं" : "Add to Favorites / पसंदीदा में जोड़ें"}
        >
          <Heart className={`w-4 h-4 stroke-[2.5] transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
        </button>
      </div>

      {/* Main Image Section (Maximized edge-to-edge object-contain fill for maximum product visibility) */}
      <div className="relative w-full aspect-square flex items-center justify-center overflow-hidden p-1">
        {product.imageUrl ? (
          <img 
            {...getProductImageProps(product.imageUrl, 400)}
            alt={product.name} 
            className={`w-full h-full max-h-full max-w-full object-contain select-none transition-transform duration-500 group-hover:scale-110 drop-shadow-xs ${!product.available ? 'grayscale brightness-90' : ''}`}
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-full h-full bg-gray-50 flex items-center justify-center rounded-xl">
            <span className="text-2xl font-black text-brand-accent/10 italic">RK</span>
          </div>
        )}
      </div>

      {/* Product Details Section */}
      <div className="flex-grow flex flex-col justify-between min-w-0">
        <div className="flex flex-col min-w-0 mb-0.5">
          {/* Promotional Collection Badges (Icon & Colored Text directly above product name) */}
          {(product.isBestSeller || product.isTrending || product.isNewArrival || product.isOffer) && (
            <div className="flex items-center gap-1.5 flex-wrap mb-1 min-w-0">
              {product.isBestSeller && (
                <span className="inline-flex items-center gap-1 text-[10px] md:text-[11px] font-extrabold text-amber-600 tracking-wide uppercase">
                  <Flame className="w-3.5 h-3.5 fill-amber-500 text-amber-500 shrink-0" /> Best Seller
                </span>
              )}
              {product.isTrending && (
                <span className="inline-flex items-center gap-1 text-[10px] md:text-[11px] font-extrabold text-violet-600 tracking-wide uppercase">
                  <Zap className="w-3.5 h-3.5 fill-violet-500 text-violet-500 shrink-0" /> Trending
                </span>
              )}
              {product.isNewArrival && (
                <span className="inline-flex items-center gap-1 text-[10px] md:text-[11px] font-extrabold text-blue-600 tracking-wide uppercase">
                  <Sparkles className="w-3.5 h-3.5 text-blue-500 shrink-0" /> New Arrival
                </span>
              )}
              {product.isOffer && (
                <span className="inline-flex items-center gap-1 text-[10px] md:text-[11px] font-extrabold text-emerald-600 tracking-wide uppercase">
                  <Tag className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> Special Offer
                </span>
              )}
            </div>
          )}

          <h3 className="font-sans font-bold text-sm md:text-base text-gray-900 tracking-tight line-clamp-2 leading-snug min-h-[36px] md:min-h-[44px] overflow-hidden">
            <HighlightMatch text={product.name} query={searchQuery} />
          </h3>
          {product.showQuantity && product.quantityValue && (
            <p className="text-xs text-gray-600 mt-0.5 font-semibold leading-none">
              {product.quantityValue} {formatQuantityUnit(product.quantityUnit || 'g')}
            </p>
          )}
        </div>

        {/* Footer Section (Price & Add CTA aligned) */}
        <div className="flex items-end justify-between gap-1 mt-auto">
          <div className="flex flex-col min-w-0">
            {/* Price section on a single horizontal line */}
            <div className="flex items-center gap-1 flex-wrap min-w-0">
              <span className="text-base md:text-lg font-black text-gray-900 leading-none">
                {formatPrice(product.price, 0)}
              </span>
              {product.mrp && product.mrp > product.price && (
                <span className="text-[11px] md:text-xs text-gray-600 line-through font-bold leading-none">
                  {formatPrice(product.mrp, 0)}
                </span>
              )}
            </div>
          </div>

          {/* Action Button */}
          <div className="flex items-center justify-end shrink-0">
            {product.available ? (
              cartQuantity > 0 ? (
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="w-8 h-8 md:w-9 md:h-9 rounded-[8px] bg-green-50 text-green-600 border border-green-200 flex items-center justify-center shadow-sm"
                  title="In Cart / कार्ट में है"
                >
                  <Check className="w-4 h-4 text-green-600 stroke-[3]" />
                </div>
              ) : (
                <button 
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToCart?.(product);
                  }}
                  className="w-8 h-8 md:w-9 md:h-9 rounded-[8px] bg-brand-accent hover:opacity-95 text-white flex items-center justify-center transition-all shadow-md hover:shadow-lg active:scale-95 cursor-pointer"
                  title="Add to Cart / कार्ट में जोड़ें"
                  aria-label="Add to Cart / कार्ट में जोड़ें"
                >
                  <Plus className="w-5 h-5 stroke-[3]" />
                </button>
              )
            ) : (
              <span className="text-[10px] text-gray-500 font-extrabold uppercase bg-gray-100 border border-gray-200 px-2.5 py-1.5 rounded-lg">
                Sold Out
              </span>
            )}
          </div>
        </div>
      </div>

    </motion.div>
  );
}

export default React.memo(ProductCard, (prevProps, nextProps) => {
  return (
    prevProps.isFavorite === nextProps.isFavorite &&
    prevProps.cartQuantity === nextProps.cartQuantity &&
    prevProps.searchQuery === nextProps.searchQuery &&
    prevProps.product.id === nextProps.product.id &&
    prevProps.product.price === nextProps.product.price &&
    prevProps.product.mrp === nextProps.product.mrp &&
    prevProps.product.name === nextProps.product.name &&
    prevProps.product.imageUrl === nextProps.product.imageUrl &&
    prevProps.product.available === nextProps.product.available &&
    prevProps.product.category === nextProps.product.category &&
    prevProps.product.showQuantity === nextProps.product.showQuantity &&
    prevProps.product.quantityValue === nextProps.product.quantityValue &&
    prevProps.product.quantityUnit === nextProps.product.quantityUnit &&
    prevProps.product.isBestSeller === nextProps.product.isBestSeller &&
    prevProps.product.isTrending === nextProps.product.isTrending &&
    prevProps.product.isNewArrival === nextProps.product.isNewArrival &&
    prevProps.product.isOffer === nextProps.product.isOffer
  );
});
